import { useState } from "react";
import { X, Database, Wrench, Loader2, CheckCircle2, AlertCircle, History } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

const AdminSettingsModal = ({ onClose, onDataChanged, onOpenAuditLog }) => {
  const [seedLoading, setSeedLoading] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [fixResult, setFixResult] = useState(null);

  const handleSeed = async () => {
    try {
      setSeedLoading(true);
      setSeedResult(null);
      const response = await axios.post(`${API}/seed`);
      setSeedResult({ success: true, ...response.data });
      if (response.data.inserted > 0) {
        toast.success(`Added ${response.data.inserted} new use cases`);
        onDataChanged?.();
      } else {
        toast.info("All seed use cases already exist");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Seed failed";
      setSeedResult({ success: false, error: message });
      toast.error(message);
    } finally {
      setSeedLoading(false);
    }
  };

  const handleFixBranding = async () => {
    try {
      setFixLoading(true);
      setFixResult(null);
      const response = await axios.post(`${API}/admin/fix-branding`);
      setFixResult({ success: true, ...response.data });
      if (response.data.updated > 0) {
        toast.success(`Fixed branding in ${response.data.updated} use cases`);
        onDataChanged?.();
      } else {
        toast.info("No branding issues found");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Fix branding failed";
      setFixResult({ success: false, error: message });
      toast.error(message);
    } finally {
      setFixLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-backdrop"
      onClick={handleBackdropClick}
      data-testid="admin-settings-modal"
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">
                Admin Settings
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Manage seed data and branding fixes
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
              data-testid="admin-settings-close-btn"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        <ScrollArea className="h-[calc(90vh-90px)]">
          <div className="p-6 space-y-6">
            {/* Sync Seed Data */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Database className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-slate-900 font-['Manrope']">
                    Sync Seed Data
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Adds any missing use cases from the code's seed data into this database.
                    Safe to run anytime — existing use cases are not touched.
                  </p>
                  {seedResult && (
                    <div
                      className={`mt-3 flex items-start gap-2 p-3 rounded-lg text-sm ${
                        seedResult.success
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                    >
                      {seedResult.success ? (
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      )}
                      <span>{seedResult.message || seedResult.error}</span>
                    </div>
                  )}
                  <Button
                    onClick={handleSeed}
                    disabled={seedLoading}
                    className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    data-testid="run-seed-btn"
                  >
                    {seedLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Sync Seed Data
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Fix Branding */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-5 h-5 text-[#FF6F40]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-slate-900 font-['Manrope']">
                    Fix Branding
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Scans all use cases and replaces any legacy text references
                    (<span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">Arcus Power</span>,{" "}
                    <span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">Arcus</span>,{" "}
                    <span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">Chariot</span>)
                    with the current Arcobi branding.
                  </p>
                  {fixResult && (
                    <div
                      className={`mt-3 flex items-start gap-2 p-3 rounded-lg text-sm ${
                        fixResult.success
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                    >
                      {fixResult.success ? (
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      )}
                      <span>{fixResult.message || fixResult.error}</span>
                    </div>
                  )}
                  <Button
                    onClick={handleFixBranding}
                    disabled={fixLoading}
                    className="mt-4 bg-[#FF6F40] hover:bg-[#E65A2C] text-white font-semibold"
                    data-testid="run-fix-branding-btn"
                  >
                    {fixLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Fixing...
                      </>
                    ) : (
                      <>
                        <Wrench className="w-4 h-4 mr-2" />
                        Fix Branding
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Audit Log */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <History className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-slate-900 font-['Manrope']">
                    Audit Log
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    View a chronological history of every use case created, edited, or deleted — including who did it and what changed.
                  </p>
                  <Button
                    onClick={() => {
                      onClose();
                      onOpenAuditLog?.();
                    }}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    data-testid="open-audit-log-btn"
                  >
                    <History className="w-4 h-4 mr-2" />
                    View Audit Log
                  </Button>
                </div>
              </div>
            </div>

            {/* Info block */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                <span className="font-semibold text-slate-700">Note:</span> These tools operate on the current environment's database only.
                Actions in preview do not affect production and vice versa. Both operations are safe to re-run — they only apply
                changes where needed.
              </p>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default AdminSettingsModal;
